import { THeader } from "../interfaces";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  FormatNumber,
  formatReportDate,
  Formatter,
} from "../formatter/Formatter";
import { PageOrientation, TDocumentDefinitions } from "pdfmake/interfaces";
import { IGenerateReport } from "../interfaces/IGenerateReport";

export const GeneratePDF = ({
  lists,
  headers,
  date,
  dateRangeFrom,
  dateRangeTo,
  batchNumber,
  reportPartnerName,
  fullName,
  formattedDateTime,
  customBase64,
  mlBannerBase64,
  totalPHP,
  totalUSD,
  totalPrincipalPHP,
  totalPrincipalUSD,
  totalFeePHP,
  totalFeeUSD,
  username,
}: IGenerateReport) => {
  pdfMake.vfs = pdfFonts.pdfMake.vfs;

  const tableRows = lists.map((row: any, index: number) => [
    (index + 1).toString(),
    ...headers.map((header: THeader) => row[header.id]),
  ]);

  const baseFontSizePx = 3.5;
  const fontSizeRem = 2;
  const fontSizePx = baseFontSizePx * fontSizeRem;

  const reportData = [];

  // Determine the date to display based on conditions
  let dateRow = [
    {
      text: "",
      bold: true,
      alignment: "center",
    },
    {
      text: "",
      alignment: "center",
    },
  ];

  // Conditional logic to set the appropriate date text
  if (date) {
    dateRow[0].text = "Date";
    dateRow[1].text = formatReportDate(date).toUpperCase();
  } else if (dateRangeFrom && dateRangeTo) {
    dateRow[0].text = "Date Range";
    dateRow[1].text = `${formatReportDate(
      dateRangeFrom,
    ).toUpperCase()} - ${formatReportDate(dateRangeTo).toUpperCase()}`;
  } else if (batchNumber) {
    dateRow[0].text = "BATCH NUMBER";
    dateRow[1].text = batchNumber;
  }

  // Adding the rows to the reportData array
  reportData.push(
    [
      {
        text: "Report Name",
        bold: true,
        alignment: "center",
      },
      {
        text: "CORPORATE DISBURSEMENT REPORT",
        bold: false,
        alignment: "center",
      },
    ],
    dateRow, // Insert the conditional date row
    [
      {
        text: "Partner Name",
        bold: true,
        alignment: "center",
      },
      {
        text: reportPartnerName.toUpperCase(),
        alignment: "center",
      },
    ],
    [
      {
        text: "Printed By",
        bold: true,
        alignment: "center",
      },
      {
        text: fullName.toUpperCase(),
        alignment: "center",
      },
    ],
    [
      {
        text: "Run Date",
        bold: true,
        alignment: "center",
      },
      {
        text: formattedDateTime,
        alignment: "center",
      },
    ],
  );
  const pxToPt = (px: any) => Math.round(px * 0.75);
  const getColumnWidth = (label: string) => {
    const normalizedLabel = label.toLowerCase();

    if (normalizedLabel.includes("date") || normalizedLabel.includes("time")) {
      return 72;
    }
    if (normalizedLabel.includes("name")) return 60;
    if (normalizedLabel.includes("contact")) return 57;
    if (normalizedLabel.includes("fee")) return 34;
    if (normalizedLabel.includes("amount")) return 55;
    if (
      normalizedLabel.includes("reference") ||
      normalizedLabel.includes("transaction") ||
      normalizedLabel.includes("batch")
    ) {
      return 55;
    }
    if (
      normalizedLabel.includes("currency") ||
      normalizedLabel.includes("status")
    ) {
      return 40;
    }
    return 48;
  };
  const tableColumnWidths = [
    pxToPt(28),
    ...headers.map((header) => pxToPt(getColumnWidth(header.label))),
  ];
  const tableCellPadding = 4;
  const tableWidth =
    tableColumnWidths.reduce((total, width) => total + width, 0) +
    tableColumnWidths.length * tableCellPadding * 2;
  const pageSize = tableWidth > 762 ? "A3" : "A4";
  const pageMargins: [number, number, number, number] = [20, 40, 40, 40];
  const pageWidth = pageSize === "A3" ? 1190.55 : 841.89;
  const availablePageWidth = pageWidth - pageMargins[0] - pageMargins[2];
  const headerLogoWidth = 220;
  const metadataTableWidth = 430;
  const tableLeftMargin = Math.max(0, (availablePageWidth - tableWidth) / 2);
  // const headerLeftMargin = tableLeftMargin;
  const headerColumnGap = Math.max(
    24,
    tableWidth - headerLogoWidth - metadataTableWidth,
  );
  const headerWidth = headerLogoWidth + metadataTableWidth + headerColumnGap;
  const headerLeftMargin = Math.max(0, (availablePageWidth - headerWidth) / 2);
  const totalsColumnWidth = tableWidth / 6 - tableCellPadding * 2;
  const totalsColumnWidths = Array.from({ length: 6 }, () => totalsColumnWidth);

  const documentDefinition: TDocumentDefinitions = {
    content: [
      {
        columns: [
          {
            image: customBase64 ? customBase64 : mlBannerBase64,
            width: headerLogoWidth,
            height: customBase64 ? 44 : 55,
            marginTop: 12,
          },
          {
            width: metadataTableWidth,
            table: {
              headerRows: 1,
              widths: ["36%", "64%"],
              body: reportData,
            },
            layout: {
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => "gray",
              vLineColor: () => "gray",
              paddingLeft: () => 4,
              paddingRight: () => 4,
              paddingTop: () => 4,
              paddingBottom: () => 4,
            },
            style: "anotherStyles",
          },
        ],
        columnGap: headerColumnGap,
        margin: [headerLeftMargin, 0, 0, 0],
      },
      { text: "\n" },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: tableColumnWidths,
          body: [
            [
              {
                text: "NO",
                bold: true,
                fontSize: 8,
                alignment: "center",
              },
              ...headers.map((header: { label: any }) => ({
                text: header.label,
                bold: true,
                fontSize: 8,
                alignment: "center",
              })),
            ],
            ...tableRows,
          ],
        },
        margin: [tableLeftMargin, 0, 0, 0],
        layout: {
          hLineWidth: () => 0.5, // Thin horizontal lines
          vLineWidth: () => 0.5, // Thin vertical lines
          hLineColor: () => "gray", // Line color
          vLineColor: () => "gray", // Line color
          paddingLeft: () => tableCellPadding,
          paddingRight: () => tableCellPadding,
          paddingTop: () => tableCellPadding,
          paddingBottom: () => tableCellPadding,
        },
        style: "table",
      },
      { text: "\n" },
      {
        table: {
          headerRows: 0,
          widths: totalsColumnWidths,
          body: [
            [
              {
                text: "Total Count ( PHP )",
                width: "50%",
                bold: false,
                alignment: "center",
              },
              {
                text: `${Formatter(totalPHP)}`,
                width: "50%",
                alignment: "center",
              },
              {
                text: "Total Pincipal ( PHP )",
                width: "50%",
                bold: false,
                alignment: "center",
              },
              {
                text: `PHP ${FormatNumber(totalPrincipalPHP)}`,
                width: "50%",
                alignment: "center",
              },
              {
                text: "Total Charge ( PHP )",
                width: "50%",
                bold: false,
                alignment: "center",
              },
              {
                text: `PHP ${FormatNumber(totalFeePHP)}`,
                width: "50%",
                alignment: "center",
              },
            ],
            [
              {
                text: "Total Count ( USD )",
                width: "50%",
                bold: false,
                alignment: "center",
              },
              {
                text: `${Formatter(totalUSD)}`,
                width: "50%",
                alignment: "center",
              },
              {
                text: "Total Pincipal ( USD )",
                width: "50%",
                bold: false,
                alignment: "center",
              },
              {
                text: `USD ${FormatNumber(totalPrincipalUSD)}`,
                width: "50%",
                alignment: "center",
              },
              {
                text: "Total Charge ( USD )",
                width: "50%",
                bold: false,
                alignment: "center",
              },
              {
                text: `USD ${FormatNumber(totalFeeUSD)}`,
                width: "50%",
                alignment: "center",
              },
            ],
          ],
        },
        margin: [tableLeftMargin, 12, 0, 0],
        layout: {
          hLineWidth: () => 0.5, // Thin horizontal lines
          vLineWidth: () => 0.5, // Thin vertical lines
          hLineColor: () => "gray", // Line color
          vLineColor: () => "gray", // Line color
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 4,
          paddingBottom: () => 4,
        },
        style: "totalsStyle",
      },
    ],
    styles: {
      header: {
        fontSize: fontSizePx,
        bold: true,
        margin: [0, 0, 0, 10] as [number, number, number, number],
        alignment: "center",
      },
      table: {
        fontSize: 8,
        alignment: "center",
      },
      totalsStyle: {
        fontSize: 7,
        // margin: [0, 5, 0, 5], // Minimized margins to save space
      },
      anotherStyle: {
        fontSize: 8,
        alignment: "center",
      },
      anotherStyles: {
        fontSize: 8,
        alignment: "center",
        margin: [0, 6, 0, 0],
      },
    },
    pageOrientation: "landscape" as PageOrientation,
    pageSize,
    pageMargins,
  };

  pdfMake
    .createPdf(documentDefinition)
    .download(`${username}_${formattedDateTime}_Report.pdf`);
};
